import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './customerDetail.css';
import CaseLogsPanel from "../../components/CaseLogsPanel";
import TestAccountPanel, { ReservedBadge } from "../../components/TestAccountPanel";
import DeviceHistoryPanel from "../../components/DeviceHistoryPanel";
import HelpdeskPanel, { CreateTicketModal } from "../../components/HelpdeskPanel";
import BASE_URL from "../../config";
import CallIcon from "@mui/icons-material/Call";
import VideoCallIcon from '@mui/icons-material/VideoCall';

import { MapContainer, TileLayer, Polyline, Popup, Marker } from 'react-leaflet';
import L from 'leaflet';
import RotatedMarker from '../customerDashboard/rotatedMarker';
import 'leaflet/dist/leaflet.css';
import CustomerChat from './customerChat';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import SystemPreferences from '../customerDashboard/systemPreferences';
import WiFiPreferences from '../customerDashboard/wifiPreferences';
import DeviceSettingsCard from '../customerDashboard/deviceSettingsCard';
import '../customerDashboard/userSettingsTabs.css';


// UK mobile network lookup by MCC/MNC, so the CRM can show a carrier name
// (e.g. "O2 UK") instead of raw MCC/MNC codes. Falls back to the raw pair if
// we don't recognize the combination (e.g. non-UK roaming, MVNO on a code we
// haven't added yet).
const MCC_MNC_CARRIERS = {
  '234-10': 'O2 UK',
  '234-11': 'Airtel Vodafone (Jersey/Guernsey)',
  '234-15': 'Vodafone UK',
  '234-16': 'Vodafone UK',
  '234-18': 'Voxi (Vodafone)',
  '234-19': 'Sky Mobile (Vodafone)',
  '234-20': 'Three UK',
  '234-27': 'Telecom Decisions (Three MVNO)',
  '234-30': 'EE',
  '234-31': 'Virgin Mobile (Virgin Media O2)',
  '234-32': 'BT Mobile (EE)',
  '234-33': 'EE',
  '234-34': 'EE',
  '234-50': 'JT (Jersey Telecom)',
  '234-55': 'Sure (Guernsey)',
  '234-58': 'Manx Telecom',
  '235-77': 'FreedomPop (EE)',
};

const carrierForCell = (mcc, mnc) => {
  if (mcc == null || mnc == null) return null;
  const key = `${mcc}-${String(mnc).padStart(2, '0')}`;
  return MCC_MNC_CARRIERS[key] || MCC_MNC_CARRIERS[`${mcc}-${mnc}`] || null;
};

// Lazy "show on map" button for a case status row that only has a Cell ID (no
// GPS fix). We keep the raw MCC/MNC/LAC/Cell numbers in the table and only ask
// the backend to convert them to coordinates via OpenCellID when the user
// actually clicks — so towers nobody looks at never cost an OpenCellID lookup.
// The backend caches each tower, so repeat clicks are free.
const CellMapButton = ({ log }) => {
  const [state, setState] = useState('idle'); // idle | loading | error
  const [errMsg, setErrMsg] = useState('');

  const handleClick = async () => {
    setState('loading');
    setErrMsg('');
    try {
      const res = await fetch(`${BASE_URL}/api/pocket/resolve-cell/${log.id}/`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.latitude != null && data.longitude != null) {
        window.open(
          `https://www.google.com/maps?q=${data.latitude},${data.longitude}`,
          '_blank',
          'noopener,noreferrer'
        );
        setState('idle');
      } else {
        setState('error');
        setErrMsg(data.error || 'Could not resolve this tower');
      }
    } catch (e) {
      setState('error');
      setErrMsg('Request failed');
    }
  };

  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <button
        onClick={handleClick}
        disabled={state === 'loading'}
        title="Convert this Cell ID to an approximate location via OpenCellID"
        style={{
          background: 'transparent', border: '1px solid #4da3ff', color: '#4da3ff',
          borderRadius: '6px', padding: '2px 8px', cursor: state === 'loading' ? 'default' : 'pointer',
          fontSize: '12px', whiteSpace: 'nowrap',
        }}
      >
        {state === 'loading' ? 'Resolving…' : '📶 Show on map'}
      </button>
      {state === 'error' && (
        <span style={{ color: '#e08a8a', fontSize: '11px', marginLeft: '6px' }}>{errMsg}</span>
      )}
    </span>
  );
};


const CustomerDetail = () => {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('customerDetails');
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [helpdeskRefresh, setHelpdeskRefresh] = useState(0);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [showBillingHistory, setShowBillingHistory] = useState(false);

  const [locationTrail, setLocationTrail] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [caseLocation, setCaseLocation] = useState(null);

  const [chatUid, setChatUid] = useState(null);
  const [chatSerial, setChatSerial] = useState(null);

  const [deviceLogs, setDeviceLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logContents, setLogContents] = useState({});
  const [logFilterFrom, setLogFilterFrom] = useState('');
  const [logFilterTo, setLogFilterTo] = useState('');
  const [liveLogs, setLiveLogs] = useState(true);

  // Case Logs (uploaded case log files, PocketLog -- distinct from the
  // "Glasses Logs" streamed debug-log tab above)
  const [caseLogs, setCaseLogs] = useState([]);
  const [caseLogsLoading, setCaseLogsLoading] = useState(false);
  const [caseLogsUid, setCaseLogsUid] = useState(null);
  const [caseLogsError, setCaseLogsError] = useState(null);

  // Diagnostics / test-mode session tab
  const [diagDeviceUid, setDiagDeviceUid] = useState(null);
  const [diagSessions, setDiagSessions] = useState([]);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState(null);
  const [diagActiveSession, setDiagActiveSession] = useState(null);
  const [diagStarting, setDiagStarting] = useState(false);
  const [diagEnding, setDiagEnding] = useState(false);
  const [diagImages, setDiagImages] = useState([]);
  const [diagImagesLoading, setDiagImagesLoading] = useState(false);
  const [diagImagesOpen, setDiagImagesOpen] = useState(false);
  const [diagImagesError, setDiagImagesError] = useState(null);
  const liveLogsIntervalRef = useRef(null);
  const [preferences, setPreferences] = useState(null);

  const [glassesStatusLogs, setGlassesStatusLogs] = useState([]);
  const [glassesStatusLoading, setGlassesStatusLoading] = useState(false);
  const [glassesStatusError, setGlassesStatusError] = useState(null);

  const [caseStatusLogs, setCaseStatusLogs] = useState([]);

  const [aiLogs, setAiLogs] = useState([]);
  const [aiLogsLoading, setAiLogsLoading] = useState(false);
  const [aiLogsError, setAiLogsError] = useState(null);
  // Date filters for the status-log tabs. `undefined` = not yet touched (defaults
  // to the most recent log day); '' = show all dates; 'YYYY-MM-DD' = that day only.
  const [glassesDateOverride, setGlassesDateOverride] = useState(undefined);
  const [caseDateOverride, setCaseDateOverride] = useState(undefined);
  const [caseStatusLoading, setCaseStatusLoading] = useState(false);
  const [caseStatusError, setCaseStatusError] = useState(null);

  const navigate = useNavigate();

  // Fetch customer
  useEffect(() => {
    fetch(`${BASE_URL}/api/customers/${id}`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        setCustomer(data);
        setFormData(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching data');
        setLoading(false);
      });
  }, [id]);

  // Fetch subscription
  useEffect(() => {
    const token = localStorage.getItem('jwt_token');
    fetch(`${BASE_URL}/api/subscription/customer/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(setSubscriptionData)
      .catch(() => setSubscriptionData(null));
  }, [id]);

  // Fetch subscription status
  useEffect(() => {
    const token = localStorage.getItem('jwt_token');
    fetch(`${BASE_URL}/api/subscription/status/${id}/`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(setSubscriptionStatus)
      .catch(() => setSubscriptionStatus(null));
  }, [id]);

  // Fetch billing history
  useEffect(() => {
    const token = localStorage.getItem('jwt_token');
    fetch(`${BASE_URL}/api/subscription/billing-history/${id}/`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(setBillingHistory)
      .catch(() => setBillingHistory([]));
  }, [id]);

  // Devices & Calls
 useEffect(() => {
  if (activeTab === 'devices' || activeTab === 'calls') {
    setDevicesLoading(true);
    setDevicesError(null); //  Reset previous error
    setDevices([]);        // Clear previous data

    const url = activeTab === 'devices'
      ? `${BASE_URL}/api/cust_devices/${id}/devices`
      : `${BASE_URL}/api/cust_calls/${id}/calls`;

    fetch(url)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        setDevices(data);
        setDevicesLoading(false);
      })
      .catch(() => {
        setDevicesError(`No ${activeTab} found`);
        setDevices([]);
        setDevicesLoading(false);
      });
  }
}, [activeTab, id]);


  useEffect(() => {
    if (activeTab === 'glassesStatusLogs') {
      setGlassesStatusLoading(true);
      setGlassesStatusError(null);
      fetch(`${BASE_URL}/api/cust_devices/${id}/glasses-status/`)
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => {
          setGlassesStatusLogs(Array.isArray(data) ? data : []);
          setGlassesStatusLoading(false);
        })
        .catch(() => {
          setGlassesStatusError('No glasses status logs found');
          setGlassesStatusLogs([]);
          setGlassesStatusLoading(false);
        });
    }
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab === 'caseStatusLogs') {
      setCaseStatusLoading(true);
      setCaseStatusError(null);
      fetch(`${BASE_URL}/api/cust_devices/${id}/case-status/`)
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => {
          setCaseStatusLogs(Array.isArray(data) ? data : []);
          setCaseStatusLoading(false);
        })
        .catch(() => {
          setCaseStatusError('No case status logs found');
          setCaseStatusLogs([]);
          setCaseStatusLoading(false);
        });
    }
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab === 'aiLogs') {
      setAiLogsLoading(true);
      setAiLogsError(null);
      fetch(`${BASE_URL}/api/cust_devices/${id}/ai-logs/`)
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => {
          setAiLogs(Array.isArray(data) ? data : []);
          setAiLogsLoading(false);
        })
        .catch(() => {
          setAiLogsError('No AI logs found');
          setAiLogs([]);
          setAiLogsLoading(false);
        });
    }
  }, [activeTab, id]);


// Logs

  const fetchLogs = useCallback(async (showLoading = false) => {
    if (showLoading) setLogsLoading(true);
    setLogsError(null);

    try {
      const deviceRes = await fetch(`${BASE_URL}/api/cust_devices/${id}/devices`);
      const deviceList = await deviceRes.json();

      if (deviceList.length === 0 || !deviceList[0].uid) {
        setDeviceLogs([]);
        return;
      }

      const uid = deviceList[0].uid;
      const token = localStorage.getItem("jwt_token");

      const res = await fetch(`${BASE_URL}/api/get-cust-device-log/?uid=${uid}`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error("Failed to fetch logs");

      const data = await res.json();
      setDeviceLogs(data);

      if (data.length > 0) {
        const contents = {};
        await Promise.all(
          data.map(async (log) => {
            try {
              const logRes = await fetch(log.log_file);
              contents[log.id] = await logRes.text();
            } catch (err) {
              console.error(`Failed to load log content for ${log.id}:`, err);
              contents[log.id] = "Unable to preview log content.";
            }
          })
        );
        setLogContents(contents);
      }
    } catch (err) {
      console.error("Error fetching device logs:", err);
      setLogsError("No logs found for this device");
    } finally {
      setLogsLoading(false);
    }
  }, [id]);

  // Case Logs -- resolves the customer's glass UID (same lookup fetchLogs
  // uses), then the connected case via the existing admin
  // get-pocket-by-device endpoint, then lists PocketLog entries for that case.
  const fetchCaseLogs = useCallback(async () => {
    setCaseLogsLoading(true);
    setCaseLogsError(null);

    try {
      const deviceRes = await fetch(`${BASE_URL}/api/cust_devices/${id}/devices`);
      const deviceList = await deviceRes.json();

      if (deviceList.length === 0 || !deviceList[0].uid) {
        setCaseLogs([]);
        setCaseLogsError("No glasses device found for this customer.");
        return;
      }

      const glassUid = deviceList[0].uid;
      const token = localStorage.getItem("jwt_token");

      const caseRes = await fetch(`${BASE_URL}/api/pocket/admin/get-pocket-by-device/${glassUid}/`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });
      if (!caseRes.ok) throw new Error("Failed to resolve connected case");
      const caseData = await caseRes.json();
      const caseUid = caseData?.connected_pocket?.uid;
      setCaseLogsUid(caseUid || null);

      if (!caseUid) {
        setCaseLogs([]);
        setCaseLogsError("This customer's glasses aren't connected to a case.");
        return;
      }

      const logsRes = await fetch(`${BASE_URL}/api/pocket/admin/logs/?case_uid=${caseUid}`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });
      if (!logsRes.ok) throw new Error("Failed to fetch case logs");
      const logsData = await logsRes.json();
      setCaseLogs(logsData);
    } catch (err) {
      console.error("Error fetching case logs:", err);
      setCaseLogsError("No case logs found for this customer.");
    } finally {
      setCaseLogsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === "caseLogs") {
      fetchCaseLogs();
    }
  }, [activeTab, id, fetchCaseLogs]);

  // Diagnostics: pull the device's test-session history (and figure out if
  // one is currently running) whenever the tab is opened. Resolves the
  // device uid from the same /devices endpoint the Devices tab uses, since
  // CustomerDetail doesn't otherwise carry it around at the top level.
  const fetchDiagSessions = useCallback(async (uid) => {
    if (!uid) return;
    setDiagLoading(true);
    setDiagError(null);
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${BASE_URL}/api/diagnostics/sessions/${uid}/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch test sessions');
      const data = await res.json();
      setDiagSessions(data);
      setDiagActiveSession(data.find((s) => s.is_active) || null);
    } catch (err) {
      console.error('Error fetching diagnostics sessions:', err);
      setDiagError('No test sessions found for this device.');
      setDiagSessions([]);
      setDiagActiveSession(null);
    } finally {
      setDiagLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'diagnostics') return;
    if (diagDeviceUid) {
      fetchDiagSessions(diagDeviceUid);
      return;
    }
    fetch(`${BASE_URL}/api/cust_devices/${id}/devices`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        const uid = data?.[0]?.uid;
        if (uid) {
          setDiagDeviceUid(uid);
          fetchDiagSessions(uid);
        } else {
          setDiagError('No device found for this customer.');
        }
      })
      .catch(() => setDiagError('Could not load device for this customer.'));
  }, [activeTab, id, diagDeviceUid, fetchDiagSessions]);

  const handleStartTest = async () => {
    if (!diagDeviceUid) return;
    setDiagStarting(true);
    setDiagError(null);
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${BASE_URL}/api/diagnostics/start/${diagDeviceUid}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed to start test session');
      await fetchDiagSessions(diagDeviceUid);
    } catch (err) {
      console.error('Error starting test session:', err);
      setDiagError('Could not start test session.');
    } finally {
      setDiagStarting(false);
    }
  };

  const handleEndTest = async () => {
    if (!diagActiveSession) return;
    setDiagEnding(true);
    setDiagError(null);
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${BASE_URL}/api/diagnostics/end/${diagActiveSession.id}/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to end test session');
      await fetchDiagSessions(diagDeviceUid);
    } catch (err) {
      console.error('Error ending test session:', err);
      setDiagError('Could not end test session.');
    } finally {
      setDiagEnding(false);
    }
  };

  const handleViewImages = async () => {
    if (!diagDeviceUid) return;
    if (diagImagesOpen) {
      setDiagImagesOpen(false);
      return;
    }
    setDiagImagesOpen(true);
    setDiagImagesLoading(true);
    setDiagImagesError(null);
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${BASE_URL}/api/diagnostics/images/${diagDeviceUid}/?limit=4`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch images');
      const data = await res.json();
      if (data.error) {
        setDiagImagesError(data.error);
      }
      setDiagImages(data.images || []);
    } catch (err) {
      console.error('Error fetching diagnostics images:', err);
      setDiagImagesError('Could not load images.');
      setDiagImages([]);
    } finally {
      setDiagImagesLoading(false);
    }
  };

  const formatDiagStatus = (statusValue) => {
    if (!statusValue) return <span style={{ color: '#888' }}>—</span>;
    const map = {
      pass: { label: 'Pass', color: '#2e7d32' },
      fail: { label: 'Fail', color: '#c62828' },
      not_charging: { label: 'Not charging', color: '#c62828' },
      not_detected: { label: 'Not detected', color: '#c62828' },
      no_data: { label: 'No data', color: '#888' },
      manual_check: { label: 'Check gallery', color: '#e67e22' },
      not_instrumented: { label: 'Not yet tracked', color: '#e67e22' },
    };
    const m = map[statusValue] || { label: statusValue, color: '#888' };
    return <span style={{ color: m.color, fontWeight: 600 }}>{m.label}</span>;
  };

  useEffect(() => {
    if (activeTab === "logs") {
      fetchLogs(true);
    }
  }, [activeTab, id, fetchLogs]);

  useEffect(() => {
    if (activeTab === "logs" && liveLogs) {
      liveLogsIntervalRef.current = setInterval(() => fetchLogs(false), 10000);
    }
    return () => clearInterval(liveLogsIntervalRef.current);
  }, [activeTab, liveLogs, fetchLogs]);

  // Preferences

useEffect(() => {
  const token = localStorage.getItem("jwt_token");
  fetch(`${BASE_URL}/api/preferences/get-customer-preferences/?cust_id=${id}`, {
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    }
  })
    .then(res => res.ok ? res.json() : Promise.reject())
    .then(data => setPreferences(data))
    .catch(err => {
      console.error("Failed to fetch preferences", err);
      setPreferences(null);
    });
}, [id]);


  // Chat

  useEffect(() => {
  if (activeTab === 'texts') {
    fetch(`${BASE_URL}/api/cust_devices/${id}/devices`)
      .then(res => res.json())
      .then(data => {
        if (data.length > 0) {
          setChatUid(data[0].uid);
          setChatSerial(data[0].serial);
        }
      })
      .catch(err => {
        console.error('Failed to load chat UID', err);
        setChatUid(null);
      });
  }
}, [activeTab, id]);


  // Locations
  useEffect(() => {
    const fetchLocations = async () => {
      if (activeTab === 'locations') {
        setLocationLoading(true);
        try {
          const deviceRes = await fetch(`${BASE_URL}/api/cust_devices/${id}/devices`);
          const deviceList = await deviceRes.json();
  
          if (deviceList.length === 0 || !deviceList[0].uid) {
            setLocationTrail([]);
            setLocationLoading(false);
            return;
          }
          const token = localStorage.getItem('jwt_token');
          const uid = deviceList[0].uid;
          const statusRes = await fetch(`${BASE_URL}/api/device-status-location/${uid}/`, {
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  },
})
          const statusData = await statusRes.json();

          const locationArray = Array.isArray(statusData) ? statusData : [statusData];

          const trail = locationArray
            .filter(loc => loc.latitude && loc.longitude)
            .map(loc => ({
              position: [parseFloat(loc.latitude), parseFloat(loc.longitude)],
              timestamp: loc.location_updated_at || loc.timestamp,
              heading: loc.compass_heading || 0,
            }));
  
          setLocationTrail(trail);

          // Fetch the case's last-known location + source (GPS vs Cell tower)
          // from the device twin. case_location_source records which one the
          // stored lat/lon came from, resolved at ingest time.
          try {
            const twinRes = await fetch(`${BASE_URL}/api/iot/twin/${uid}/`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            });
            if (twinRes.ok) {
              const twin = await twinRes.json();
              if (twin && twin.case_latitude != null && twin.case_longitude != null) {
                setCaseLocation({
                  position: [parseFloat(twin.case_latitude), parseFloat(twin.case_longitude)],
                  source: twin.case_location_source || 'gps',
                  reportedAt: twin.case_location_reported_at,
                  cell: {
                    mcc: twin.case_cell_mcc,
                    mnc: twin.case_cell_mnc,
                    lac: twin.case_cell_lac,
                    cell_id: twin.case_cell_id,
                  },
                });
              } else {
                setCaseLocation(null);
              }
            }
          } catch (twinErr) {
            console.error('Failed to fetch case twin location', twinErr);
          }
        } catch (err) {
          console.error('Failed to fetch location data', err);
        } finally {
          setLocationLoading(false);
        }
      }
    };
  
    fetchLocations();
  }, [activeTab, id]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = () => {
    fetch(`${BASE_URL}/api/customers/${id}/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(updated => {
        setCustomer(updated);
        setIsEditing(false);
      })
      .catch(() => setError('Error updating data'));
  };

  const renderBillingHistory = () => {
    if (!billingHistory.length) return <p>No billing history available.</p>;

    return (
      <table className="credit-history-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Plan</th>
            <th>Period</th>
            <th>Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {billingHistory.map((entry) => (
            <tr key={entry.id}>
              <td>{new Date(entry.created_at).toLocaleDateString()}</td>
              <td>{entry.subscription_plan}</td>
              <td>
                {new Date(entry.period_start).toLocaleDateString()} - {new Date(entry.period_end).toLocaleDateString()}
              </td>
              <td style={{ color: entry.status === 'paid' || entry.status === 'succeeded' ? 'green' : 'red', fontWeight: '600' }}>
                £{parseFloat(entry.amount_due).toFixed(2)}
              </td>
              <td>
                <span className={`payment-status ${entry.status}`}>
                  {entry.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: "Active", color: "#4caf50" },
      past_due: { label: "Payment Failed", color: "#ff9800" },
      canceled: { label: "Canceled", color: "#9e9e9e" },
      suspended: { label: "Suspended", color: "#f44336" },
    };

    const config = statusConfig[status] || { label: status, color: "#9e9e9e" };
    return (
      <span style={{
        backgroundColor: config.color,
        color: "white",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "0.85em",
        fontWeight: "bold",
        textTransform: "uppercase"
      }}>
        {config.label}
      </span>
    );
  };

  const formatBatteryPercent = (value) => {
    if (value == null) return '—';
    return `${value} %`;
  };

  const formatChargingStatus = (value) => {
    if (value == null) return '—';
    return value ? 'Yes' : 'No';
  };

  const formatSignalLevel = (value) => {
    if (value == null) return '—';
    return `${value} dBm`;
  };

  const formatStationCount = (value) => {
    if (value == null) return '—';
    return value;
  };

  const formatOperator = (value) => {
    if (!value) return '—';
    return value;
  };

  const formatWearingStatus = (device) => {
    if (device?.wearing_display) return device.wearing_display;

    const wearingLabels = {
      0: 'Unknown',
      1: 'Not Worn',
      2: 'Worn',
      3: 'Fault',
    };

    if (device?.glasses_wearing != null) {
      return wearingLabels[device.glasses_wearing] ?? 'Unknown';
    }

    return '—';
  };

  const formatGlassesInside = (device) => {
    if (device?.glasses_inside) return device.glasses_inside;
    return '—';
  };

  const formatTimestamp = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString();
  };

  const formatCoordinate = (value) => {
    if (value == null || value === '') return '—';
    return value;
  };

  const formatLocationSource = (value) => {
    if (value === 'gps') return 'GPS';
    if (value === 'cell') return 'Cell tower (approx.)';
    return '—';
  };

  const formatCellTower = (log) => {
    if (log?.cell_id == null) return '—';
    const carrier = carrierForCell(log.cell_mcc, log.cell_mnc);
    const parts = [carrier || `MCC ${log.cell_mcc ?? '—'}, MNC ${log.cell_mnc ?? '—'}`];
    if (log.cell_lac != null) parts.push(`LAC ${log.cell_lac}`);
    parts.push(`Cell ${log.cell_id}`);
    return parts.join(', ');
  };

  // Local YYYY-MM-DD key for a timestamp (matches <input type="date"> value format).
  const toDateKey = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-CA');
  };

  // Reusable filter bar for the status-log tabs. `activeDate` is '' for "all".
  const renderDateFilterBar = (activeDate, total, shown, setOverride, latestDate) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '0 0 12px', flexWrap: 'wrap' }}>
      <label style={{ color: '#4da3ff', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        Date:
        <input
          type="date"
          value={activeDate || ''}
          max={latestDate || undefined}
          onChange={(e) => setOverride(e.target.value)}
          style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #4da3ff', background: '#fff', color: '#222' }}
        />
      </label>
      <button
        onClick={() => setOverride('')}
        style={{ background: 'transparent', border: '1px solid #4da3ff', color: '#4da3ff', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer', fontSize: '13px' }}
      >
        Show all dates
      </button>
      {latestDate && (
        <button
          onClick={() => setOverride(latestDate)}
          style={{ background: 'transparent', border: '1px solid #4da3ff', color: '#4da3ff', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer', fontSize: '13px' }}
        >
          Latest day
        </button>
      )}
      <span style={{ color: '#4da3ff', fontSize: '12px' }}>
        Showing {shown} of {total} logs{activeDate ? ` for ${activeDate}` : ' (all dates)'}
      </span>
    </div>
  );

  const renderLocationLink = (log) => {
    const lat = log?.latitude;
    const lon = log?.longitude;
    if (lat == null || lat === '' || lon == null || lon === '') {
      // No GPS fix on this row — if it has a Cell ID, offer a lazy "show on map"
      // button that resolves it via OpenCellID only on click. Otherwise dash.
      if (log?.cell_id != null) return <CellMapButton log={log} />;
      return '—';
    }
    const url = `https://www.google.com/maps?q=${lat},${lon}`;
    const isApprox = log?.location_source === 'cell';
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={isApprox ? 'Approximate (cell tower) location' : 'GPS location'}
        style={{ color: '#4da3ff', textDecoration: 'none', whiteSpace: 'nowrap' }}
      >
        📍 Open map{isApprox ? ' (approx.)' : ''}
      </a>
    );
  };

  const formatGlassesDetected = (value) => {
    if (value == null) return '—';
    return value ? 'Yes' : 'No';
  };


  const renderTabContent = () => {
    switch (activeTab) {
      case 'customerDetails':
        return (
          <div className="customer-details-grid">
            {/* Personal Information Card */}
            <div className="info-card">
              <div className="card-header">
                <h3 className="card-title">Personal Information</h3>
                {!isEditing && (
                  <button className="edit-btn-small button-cust" onClick={() => setIsEditing(true)}>Edit</button>
                )}
              </div>
              <div className="card-body">
                <div className="info-row">
                  <strong>Customer ID:</strong>
                  <span>{customer.id}</span>
                </div>
                <div className="info-row">
                  <strong>Username:</strong>
                  {isEditing ? <input className='input-cust' type="text" name="username" value={formData.username} onChange={handleChange} readOnly /> : <span>{customer.username}</span>}
                </div>
                <div className="info-row">
                  <strong>First Name:</strong>
                  {isEditing ? <input className='input-cust' type="text" name="first_name" value={formData.first_name} onChange={handleChange} /> : <span>{customer.first_name}</span>}
                </div>
                <div className="info-row">
                  <strong>Last Name:</strong>
                  {isEditing ? <input className='input-cust' type="text" name="last_name" value={formData.last_name} onChange={handleChange} /> : <span>{customer.last_name}</span>}
                </div>
                <div className="info-row">
                  <strong>Email:</strong>
                  {isEditing ? <input className='input-cust' type="email" name="email" value={formData.email} onChange={handleChange} readOnly /> : <span>{customer.email}</span>}
                </div>
                <div className="info-row">
                  <strong>Contact Number:</strong>
                  {isEditing ? <input className='input-cust' type="text" name="contact_number" value={formData.contact_number} onChange={handleChange} /> : <span>{customer.contact_number}</span>}
                </div>
                {isEditing ? (
                  <>
                    <div className="info-row">
                      <strong>Address:</strong>
                      <input className='input-cust' type="text" name="address_line_1" value={formData.address_line_1 || ''} onChange={handleChange} />
                    </div>
                    <div className="info-row">
                      <strong>City:</strong>
                      <input className='input-cust' type="text" name="city" value={formData.city || ''} onChange={handleChange} />
                    </div>
                    <div className="info-row">
                      <strong>Postcode:</strong>
                      <input className='input-cust' type="text" name="postcode" value={formData.postcode || ''} onChange={handleChange} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="info-row">
                      <strong>Address:</strong>
                      <span>{customer.address_line_1 || '-'}</span>
                    </div>
                    <div className="info-row">
                      <strong>City:</strong>
                      <span>{customer.city || '-'}</span>
                    </div>
                    <div className="info-row">
                      <strong>Postcode:</strong>
                      <span>{customer.postcode || '-'}</span>
                    </div>
                  </>
                )}
                <div className="info-row">
                  <strong>Status:</strong>
                  {isEditing ? (
                    <label className="checkbox-label">
                      <input className='input-cust-checkbox' type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} />
                      Active
                    </label>
                  ) : (
                    <span className={`status-badge ${customer.is_active ? 'status-active' : 'status-inactive'}`}>
                      {customer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </div>
                {isEditing && (
                  <div className="card-actions">
                    <button className="save-btn button-cust" onClick={handleSave}>Save Changes</button>
                    <button className="cancel-btn button-cust-secondary" onClick={() => { setIsEditing(false); setFormData(customer); }}>Cancel</button>
                  </div>
                )}
              </div>
            </div>

            {/* Subscription Card */}
            <div className="info-card">
              <div className="card-header">
                <h3 className="card-title">Subscription Details</h3>
              </div>
              <div className="card-body">
                {subscriptionData ? (
                  <>
                    <div className="info-row">
                      <strong>Current Plan:</strong>
                      <span className="highlight-text">{subscriptionData.planName}</span>
                    </div>
                    <div className="info-row">
                      <strong>Start Date:</strong>
                      <span>{new Date(subscriptionData.start_date).toLocaleDateString()}</span>
                    </div>
                    <div className="info-row">
                      <strong>Monthly Price:</strong>
                      <span className="price-text">£{parseFloat(subscriptionData.price).toFixed(2)}/month</span>
                    </div>
                    {subscriptionData.status && (
                      <div className="info-row">
                        <strong>Status:</strong>
                        <span>{getStatusBadge(subscriptionData.status)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="no-data-message">No active subscription</p>
                )}
              </div>
            </div>

            {/* Subscription Status Card */}
            {subscriptionStatus && (subscriptionStatus.status || subscriptionStatus.plan_price != null) && (
              <div className="info-card balance-card">
                <div className="card-header">
                  <h3 className="card-title">Subscription Status</h3>
                </div>
                <div className="card-body">
                  <div className="balance-details">
                    <div className="balance-item">
                      <span className="label">Status</span>
                      <span className="value">{getStatusBadge(subscriptionStatus.status)}</span>
                    </div>
                    <div className="balance-item">
                      <span className="label">Monthly Price</span>
                      <span className="value">{subscriptionStatus.plan_price != null && !Number.isNaN(parseFloat(subscriptionStatus.plan_price)) ? `£${parseFloat(subscriptionStatus.plan_price).toFixed(2)}` : "—"}</span>
                    </div>
                    {subscriptionStatus.next_billing_date && (
                      <div className="balance-item">
                        <span className="label">Next Billing</span>
                        <span className="value">{new Date(subscriptionStatus.next_billing_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {subscriptionStatus.grace_period_end && (
                      <div className="balance-item">
                        <span className="label">Grace Period Ends</span>
                        <span className="value" style={{ color: '#ff9800', fontWeight: 'bold' }}>
                          {new Date(subscriptionStatus.grace_period_end).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Billing History Card */}
            <div className="info-card full-width">
              <div className="card-header">
                <h3 className="card-title">Billing History</h3>
                <button
                  className="toggle-btn button-cust-secondary"
                  onClick={() => setShowBillingHistory(prev => !prev)}
                >
                  {showBillingHistory ? 'Hide' : 'Show'}
                </button>
              </div>
              {showBillingHistory && (
                <div className="card-body">
                  {renderBillingHistory()}
                </div>
              )}
            </div>
          </div>
        );

      case 'devices':
        return (
          <div className="devices-section">
            {devicesLoading ? <div>Loading Devices...</div> :
              devicesError ? <div>{devicesError}</div> : (
                <>
                  <div className="devices-table-wrapper">
                    <table className="devices-table">
                      <thead>
                        <tr>
                          <th>Device Serial</th><th>Mac Address</th><th>UID</th><th>Status</th>
                          <th>Battery Status</th><th>Glasses Charging</th><th>Wearing</th><th>Case Status</th>
                          <th>Case Charging</th><th>Glasses Inside</th><th>LTE Signal</th><th>Station Count</th><th>Operator</th>
                          <th>Glasses Last</th><th>Case Last</th>
                          <th>Batch ID</th><th>Current Version</th><th>Request Count</th><th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devices.map(device => (
                          <tr key={device.id}>
                            <td data-label="Device Serial">{device.serial}</td>
                            <td data-label="Mac Address">{device.mac}</td>
                            <td data-label="UID">{device.uid}</td>
                            <td data-label="Status">{device.status}</td>
                            <td data-label="Battery Status">{formatBatteryPercent(device.battery_status)}</td>
                            <td data-label="Glasses Charging">{formatChargingStatus(device.glasses_charging)}</td>
                            <td data-label="Wearing">{formatWearingStatus(device)}</td>
                            <td data-label="Case Status">{formatBatteryPercent(device.case_status)}</td>
                            <td data-label="Case Charging">{formatChargingStatus(device.case_charging)}</td>
                            <td data-label="Glasses Inside">{formatGlassesInside(device)}</td>
                            <td data-label="LTE Signal">{formatSignalLevel(device.lte_signal_level)}</td>
                            <td data-label="Station Count">{formatStationCount(device.station_count)}</td>
                            <td data-label="Operator">{formatOperator(device.operator)}</td>
                            <td data-label="Glasses Last">{formatTimestamp(device.glasses_last)}</td>
                            <td data-label="Case Last">{formatTimestamp(device.case_last)}</td>
                            <td data-label="Batch ID">{device.batch_id}</td>
                            <td data-label="Current Version">{device.current_version}</td>
                            <td data-label="Request Count">{device.req_count}</td>
                            <td data-label="Details">{device.details}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="devices-cards">
                    {devices.map(device => (
                      <div className="info-card device-info-card" key={device.id}>
                        <div className="card-header">
                          <h3 className="card-title">{device.serial || 'Device'}</h3>
                          <span className={`status-badge ${device.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}`}>
                            {device.status}
                          </span>
                        </div>
                        <div className="card-body">
                          <div className="device-fields-grid">
                            <div className="info-row">
                              <strong>Mac Address</strong>
                              <span>{device.mac || '—'}</span>
                            </div>
                            <div className="info-row">
                              <strong>UID</strong>
                              <span>{device.uid || '—'}</span>
                            </div>
                            <div className="info-row">
                              <strong>Battery Status</strong>
                              <span>{formatBatteryPercent(device.battery_status)}</span>
                            </div>
                            <div className="info-row">
                              <strong>Glasses Charging</strong>
                              <span>{formatChargingStatus(device.glasses_charging)}</span>
                            </div>
                            <div className="info-row">
                              <strong>Wearing</strong>
                              <span>{formatWearingStatus(device)}</span>
                            </div>
                            <div className="info-row">
                              <strong>Case Status</strong>
                              <span>{formatBatteryPercent(device.case_status)}</span>
                            </div>
                            <div className="info-row">
                              <strong>Case Charging</strong>
                              <span>{formatChargingStatus(device.case_charging)}</span>
                            </div>
                            <div className="info-row">
                              <strong>Glasses Inside</strong>
                              <span>{formatGlassesInside(device)}</span>
                            </div>
                            <div className="info-row">
                              <strong>LTE Signal Level</strong>
                              <span>{formatSignalLevel(device.lte_signal_level)}</span>
                            </div>
                            <div className="info-row">
                              <strong>Station Count</strong>
                              <span>{formatStationCount(device.station_count)}</span>
                            </div>
                            <div className="info-row">
                              <strong>Operator</strong>
                              <span>{formatOperator(device.operator)}</span>
                            </div>
                            <div className="info-row">
                              <strong>Glasses Last</strong>
                              <span>{formatTimestamp(device.glasses_last) || '—'}</span>
                            </div>
                            <div className="info-row">
                              <strong>Case Last</strong>
                              <span>{formatTimestamp(device.case_last) || '—'}</span>
                            </div>
                            <div className="info-row">
                              <strong>Batch ID</strong>
                              <span>{device.batch_id ?? '—'}</span>
                            </div>
                            <div className="info-row">
                              <strong>Current Version</strong>
                              <span>{device.current_version ?? '—'}</span>
                            </div>
                            <div className="info-row">
                              <strong>Request Count</strong>
                              <span>{device.req_count ?? '—'}</span>
                            </div>
                            {device.details && (
                              <div className="info-row device-details-row">
                                <strong>Details</strong>
                                <span>{device.details}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
          </div>
        );

      case 'glassesStatusLogs': {
        const gLatest = glassesStatusLogs[0] ? toDateKey(glassesStatusLogs[0].timestamp) : '';
        const gActive = glassesDateOverride === undefined ? gLatest : glassesDateOverride;
        const gRows = gActive ? glassesStatusLogs.filter((l) => toDateKey(l.timestamp) === gActive) : glassesStatusLogs;
        return (
          <div className="devices-section">
            {glassesStatusLoading ? <div>Loading glasses status logs...</div> :
              glassesStatusError ? <div>{glassesStatusError}</div> :
              glassesStatusLogs.length === 0 ? <div>No glasses status logs found for this customer.</div> : (
                <>
                  {renderDateFilterBar(gActive, glassesStatusLogs.length, gRows.length, setGlassesDateOverride, gLatest)}
                  {gRows.length === 0 ? (
                    <div style={{ color: '#4da3ff', padding: '8px' }}>No glasses logs on {gActive}.</div>
                  ) : (
                <div className="devices-table-wrapper">
                  <table className="devices-table">
                    <thead>
                      <tr>
                        <th>Device UID</th>
                        <th>Device Serial</th>
                        <th>Battery</th>
                        <th>Latitude</th>
                        <th>Longitude</th>
                        <th>Slave Version</th>
                        <th>Master Version</th>
                        <th>Battery Updated At</th>
                        <th>Location Updated At</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gRows.map((log) => (
                        <tr key={log.id}>
                          <td data-label="Device UID">{log.device_uid}</td>
                          <td data-label="Device Serial">{log.device_serial}</td>
                          <td data-label="Battery">{formatBatteryPercent(log.battery)}</td>
                          <td data-label="Latitude">{formatCoordinate(log.latitude)}</td>
                          <td data-label="Longitude">{formatCoordinate(log.longitude)}</td>
                          <td data-label="Slave Version">{log.slave_version || '—'}</td>
                          <td data-label="Master Version">{log.master_version || '—'}</td>
                          <td data-label="Battery Updated At">{formatTimestamp(log.battery_updated_at) || '—'}</td>
                          <td data-label="Location Updated At">{formatTimestamp(log.location_updated_at) || '—'}</td>
                          <td data-label="Timestamp">{formatTimestamp(log.timestamp) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                  )}
                </>
              )}
          </div>
        );
      }

      case 'caseStatusLogs': {
        const cLatest = caseStatusLogs[0] ? toDateKey(caseStatusLogs[0].timestamp) : '';
        const cActive = caseDateOverride === undefined ? cLatest : caseDateOverride;
        const cRows = cActive ? caseStatusLogs.filter((l) => toDateKey(l.timestamp) === cActive) : caseStatusLogs;
        return (
          <div className="devices-section">
            {caseStatusLoading ? <div>Loading case status logs...</div> :
              caseStatusError ? <div>{caseStatusError}</div> :
              caseStatusLogs.length === 0 ? <div>No case status logs found for this customer.</div> : (
                <>
                  {renderDateFilterBar(cActive, caseStatusLogs.length, cRows.length, setCaseDateOverride, cLatest)}
                  {cRows.length === 0 ? (
                    <div style={{ color: '#4da3ff', padding: '8px' }}>No case logs on {cActive}.</div>
                  ) : (
                <div className="devices-table-wrapper">
                  <table className="devices-table">
                    <thead>
                      <tr>
                        <th>Case UID</th>
                        <th>Case Serial</th>
                        <th>Glass UID</th>
                        <th>State</th>
                        <th>Charging</th>
                        <th>Battery</th>
                        <th>Glasses Detected</th>
                        <th>Glasses Inside</th>
                        <th>LTE Signal Level</th>
                        <th>Station Count</th>
                        <th>Operator</th>
                        <th>Latitude</th>
                        <th>Longitude</th>
                        <th>Location Source</th>
                        <th>Cell Tower</th>
                        <th>Location</th>
                        <th>Master Version</th>
                        <th>Slave Version</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cRows.map((log) => (
                        <tr key={log.id}>
                          <td data-label="Case UID">{log.case_uid}</td>
                          <td data-label="Case Serial">{log.case_serial}</td>
                          <td data-label="Glass UID">{log.glass_uid || '—'}</td>
                          <td data-label="State">{log.state ?? '—'}</td>
                          <td data-label="Charging">{formatChargingStatus(log.charging)}</td>
                          <td data-label="Battery">{formatBatteryPercent(log.battery)}</td>
                          <td data-label="Glasses Detected">{formatGlassesDetected(log.glasses_detected)}</td>
                          <td data-label="Glasses Inside">{log.glasses_inside || '—'}</td>
                          <td data-label="LTE Signal Level">{formatSignalLevel(log.lte_signal_level)}</td>
                          <td data-label="Station Count">{formatStationCount(log.station_count)}</td>
                          <td data-label="Operator">{formatOperator(log.operator)}</td>
                          <td data-label="Latitude">{formatCoordinate(log.latitude)}</td>
                          <td data-label="Longitude">{formatCoordinate(log.longitude)}</td>
                          <td data-label="Location Source">{formatLocationSource(log.location_source)}</td>
                          <td data-label="Cell Tower">{formatCellTower(log)}</td>
                          <td data-label="Location">{renderLocationLink(log)}</td>
                          <td data-label="Master Version">{log.master_version || '—'}</td>
                          <td data-label="Slave Version">{log.slave_version || '—'}</td>
                          <td data-label="Timestamp">{formatTimestamp(log.timestamp) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                  )}
                </>
              )}
          </div>
        );
      }

      case 'aiLogs':
        return (
          <div className="devices-section">
            {aiLogsLoading ? <div>Loading AI logs...</div> :
              aiLogsError ? <div>{aiLogsError}</div> :
              aiLogs.length === 0 ? <div>No AI logs found for this customer. AI logs are only recorded while 'Keeping Logs' is enabled in System Preferences.</div> : (
                <div className="devices-table-wrapper">
                  <table className="devices-table">
                    <thead>
                      <tr>
                        <th>Device UID</th>
                        <th>Device Serial</th>
                        <th>What AI Heard</th>
                        <th>What AI Replied</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiLogs.map((log) => (
                        <tr key={log.id}>
                          <td data-label="Device UID">{log.device_uid}</td>
                          <td data-label="Device Serial">{log.device_serial}</td>
                          <td data-label="What AI Heard">{log.request_text || '—'}</td>
                          <td data-label="What AI Replied">{log.response_text || '—'}</td>
                          <td data-label="Timestamp">{formatTimestamp(log.timestamp) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        );

      case 'calls':
        return (
          <div>
      
<div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
<button
  style={{
    backgroundColor: 'red', color: 'white', border: 'none',
    padding: '10px 20px', borderRadius: '5px',
    cursor: 'pointer', display: 'flex', alignItems: 'center',
  }}
>
  <CallIcon style={{ marginRight: '8px' }} /> Audio Call
</button>

<button
  style={{
    backgroundColor: 'green', color: 'white', border: 'none',
    padding: '10px 20px', borderRadius: '5px',
    cursor: 'pointer', display: 'flex', alignItems: 'center',
  }}
>
  <VideoCallIcon style={{ marginRight: '8px' }} /> Video Call
</button>
</div>



            {devicesLoading ? <div>Loading Calls...</div> :
              devicesError ? <div>{devicesError}</div> : (
                <table>
                  <thead>
                    <tr><th>Call From</th><th>Call To</th><th>Start</th><th>End</th><th>Duration</th></tr>
                  </thead>
                  <tbody>
                    {devices.map(call => (
                      <tr key={call.id}>
                        <td>{call.call_from}</td><td>{call.call_to}</td>
                        <td>{call.start_time}</td><td>{call.end_time}</td><td>{call.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        );


        case "caseLogs":
          return (
            <div className="logs-container">
              <h2 style={{ color: "white", margin: '0 0 1rem 0' }}>Case Logs</h2>
              {caseLogsLoading ? (
                <p>Loading case logs...</p>
              ) : caseLogsError ? (
                <p style={{ color: '#888' }}>{caseLogsError}</p>
              ) : caseLogs.length > 0 ? (
                <table>
                  <thead>
                    <tr><th>Uploaded</th><th>Description</th><th>File</th></tr>
                  </thead>
                  <tbody>
                    {caseLogs.map(log => (
                      <tr key={log.id}>
                        <td>{log.received_at ? new Date(log.received_at).toLocaleString() : '-'}</td>
                        <td>{log.description || '-'}</td>
                        <td>
                          {log.log_file_url ? (
                            <a href={log.log_file_url} target="_blank" rel="noopener noreferrer">
                              {log.file_name || 'Download'}
                            </a>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No case log files uploaded yet.</p>
              )}
              {caseLogsUid && (
                <div style={{ marginTop: '1.5rem' }}>
                  <CaseLogsPanel caseUid={caseLogsUid} showUploaded={false} onChange={fetchCaseLogs} />
                </div>
              )}
            </div>
          );

        case "diagnostics":
          return (
            <div className="devices-section">
              <h2 style={{ color: "black" }} className="cd-loc-title">Diagnostics</h2>
              {diagError && <div style={{ color: '#b00020', marginBottom: '10px' }}>{diagError}</div>}
              {!diagDeviceUid ? (
                <div>Loading device...</div>
              ) : (
                <>
                  <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {diagActiveSession ? (
                      <>
                        <span style={{ color: '#e67e22', fontWeight: 600 }}>
                          Test running since {formatTimestamp(diagActiveSession.started_at)}
                        </span>
                        <button
                          onClick={handleEndTest}
                          disabled={diagEnding}
                          style={{ padding: '6px 16px', borderRadius: '6px', background: '#2e7d32', color: '#fff', border: 'none', cursor: 'pointer' }}
                        >
                          {diagEnding ? 'Finishing…' : 'Done'}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleStartTest}
                        disabled={diagStarting}
                        style={{ padding: '6px 16px', borderRadius: '6px', background: '#4da3ff', color: '#fff', border: 'none', cursor: 'pointer' }}
                      >
                        {diagStarting ? 'Starting…' : 'Start Test'}
                      </button>
                    )}
                    <button
                      onClick={handleViewImages}
                      style={{ padding: '6px 16px', borderRadius: '6px', background: '#555', color: '#fff', border: 'none', cursor: 'pointer' }}
                    >
                      {diagImagesOpen ? 'Hide Images' : 'View Images'}
                    </button>
                  </div>

                  {diagImagesOpen && (
                    <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #444', borderRadius: '8px' }}>
                      {diagImagesLoading ? (
                        <div>Loading images...</div>
                      ) : diagImagesError ? (
                        <div style={{ color: '#e67e22' }}>{diagImagesError}</div>
                      ) : diagImages.length === 0 ? (
                        <div>No stored images found for this device yet.</div>
                      ) : (
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          {diagImages.map((img) => (
                            <div key={img.filename} style={{ textAlign: 'center' }}>
                              <a href={`${BASE_URL}${img.view_url}`} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={`${BASE_URL}${img.view_url}`}
                                  alt={img.filename}
                                  style={{ width: '160px', height: '160px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #666' }}
                                />
                              </a>
                              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '4px', maxWidth: '160px' }}>
                                {formatTimestamp(img.created)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {diagLoading ? (
                    <div>Loading test sessions...</div>
                  ) : diagSessions.length === 0 ? (
                    <div>No test sessions yet for this device.</div>
                  ) : (
                    <div className="devices-table-wrapper">
                      <table className="devices-table">
                        <thead>
                          <tr>
                            <th>Started</th>
                            <th>Ended</th>
                            <th>Engineer</th>
                            <th>Camera</th>
                            <th>Mic</th>
                            <th>GPS</th>
                            <th>LTE</th>
                            <th>Case Charging</th>
                            <th>Glasses Charging</th>
                            <th>Glasses Detected</th>
                            <th>Case↔Glasses WiFi</th>
                            <th>Compass</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diagSessions.map((s) => (
                            <tr key={s.id}>
                              <td data-label="Started">{formatTimestamp(s.started_at)}</td>
                              <td data-label="Ended">{s.ended_at ? formatTimestamp(s.ended_at) : (s.is_active ? 'Running' : '—')}</td>
                              <td data-label="Engineer">{s.started_by || '—'}</td>
                              <td data-label="Camera">{formatDiagStatus(s.result?.camera?.status)}</td>
                              <td data-label="Mic" title={s.result?.mic?.last_heard || ''}>{formatDiagStatus(s.result?.mic?.status)}</td>
                              <td data-label="GPS">{formatDiagStatus(s.result?.gps?.status)}</td>
                              <td data-label="LTE">{formatDiagStatus(s.result?.lte?.status)}</td>
                              <td data-label="Case Charging">{formatDiagStatus(s.result?.case_charging?.status)}</td>
                              <td data-label="Glasses Charging">{formatDiagStatus(s.result?.glasses_charging?.status)}</td>
                              <td data-label="Glasses Detected">{formatDiagStatus(s.result?.glasses_detected?.status)}</td>
                              <td data-label="Case↔Glasses WiFi">{formatDiagStatus(s.result?.wifi_case_link?.status)}</td>
                              <td data-label="Compass">{formatDiagStatus(s.result?.compass?.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          );

        case "logs":
            if (!(preferences?.logs_mode === 1 && preferences?.debug_mode === 1)) {
    return <p style={{ color: "red" }}>Logs are disabled for this customer.</p>;
  }
  return (
    <div className="logs-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h2 style={{ color: "white", margin: 0 }}>Device Logs</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white', cursor: 'pointer', userSelect: 'none' }}>
          <span style={{ fontSize: '0.9rem' }}>Live Logs</span>
          <div
            onClick={() => setLiveLogs(prev => !prev)}
            style={{
              width: '44px', height: '24px', borderRadius: '12px', position: 'relative', cursor: 'pointer',
              backgroundColor: liveLogs ? '#4caf50' : '#555', transition: 'background-color 0.2s',
            }}
          >
            <div style={{
              width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'white',
              position: 'absolute', top: '3px', transition: 'left 0.2s',
              left: liveLogs ? '23px' : '3px',
            }} />
          </div>
          <span style={{ fontSize: '0.8rem', color: liveLogs ? '#4caf50' : '#888' }}>
            {liveLogs ? 'On' : 'Off'}
          </span>
        </label>
      </div>
      <div className="log-filter">
        <label>From</label>
        <input
          type="datetime-local"
          value={logFilterFrom}
          onChange={e => setLogFilterFrom(e.target.value)}
        />
        <label>To</label>
        <input
          type="datetime-local"
          value={logFilterTo}
          onChange={e => setLogFilterTo(e.target.value)}
        />
        {(logFilterFrom || logFilterTo) && (
          <button className="log-filter-clear" onClick={() => { setLogFilterFrom(''); setLogFilterTo(''); }}>
            Clear
          </button>
        )}
      </div>
      {logsLoading ? (
        <p>Loading logs...</p>
      ) : logsError ? (
        <p>{logsError}</p>
      ) : deviceLogs.length > 0 ? (() => {
        const allText = deviceLogs.map(log => logContents[log.id] || "").join("\n");
        const fromDate = logFilterFrom ? new Date(logFilterFrom) : null;
        const toDate = logFilterTo ? new Date(logFilterTo + ':59') : null;
        const filtered = (fromDate || toDate)
          ? allText.split("\n").filter(line => {
              const m = line.match(/\[(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\]/);
              if (!m) return true;
              const d = new Date(m[1]);
              if (fromDate && d < fromDate) return false;
              if (toDate && d > toDate) return false;
              return true;
            }).join("\n")
          : allText;
        return filtered
          ? <pre className="log-preview">{filtered}</pre>
          : <p style={{ color: '#888' }}>No logs match the selected time range.</p>;
      })() : (
        <p>No logs available.</p>
      )}
    </div>
  );


        case 'locations': {
          const hasData = locationTrail.length > 0 || !!caseLocation;
          const mapCenter = locationTrail.length > 0
            ? locationTrail[0].position
            : (caseLocation ? caseLocation.position : [0, 0]);
          const isCell = caseLocation?.source === 'cell';
          const caseIcon = caseLocation
            ? L.divIcon({
                className: 'cd-source-marker',
                html: `<div style="font-size:26px;line-height:1;text-align:center;filter:drop-shadow(0 1px 2px rgba(0,0,0,.5))">${isCell ? '📶' : '🛰️'}</div>`,
                iconSize: [30, 30],
                iconAnchor: [15, 15],
                popupAnchor: [0, -14],
              })
            : null;
          const cellCarrier = caseLocation ? carrierForCell(caseLocation.cell.mcc, caseLocation.cell.mnc) : null;
          const cellStr = caseLocation
            ? [
                cellCarrier || `MCC ${caseLocation.cell.mcc ?? '—'}, MNC ${caseLocation.cell.mnc ?? '—'}`,
                caseLocation.cell.lac != null ? `LAC ${caseLocation.cell.lac}` : null,
                caseLocation.cell.cell_id != null ? `Cell ${caseLocation.cell.cell_id}` : null,
              ].filter(Boolean).join(', ')
            : '';
          return (
            <div className="cd-loc-card">
              <h2  style={{ color: "black" }} className="cd-loc-title">Last Known Locations</h2>

              {caseLocation && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  margin: '0 0 12px', padding: '10px 14px', borderRadius: '8px',
                  background: isCell ? '#fff4e5' : '#e8f5e9', color: '#222',
                  fontSize: '14px', maxWidth: '560px',
                }}>
                  <span style={{ fontSize: '22px', lineHeight: 1 }}>{isCell ? '📶' : '🛰️'}</span>
                  <span>
                    <strong>Case last location:</strong>{' '}
                    {isCell ? 'Cell tower (approximate)' : 'GPS'}
                    {' — '}
                    {caseLocation.position[0].toFixed(6)}, {caseLocation.position[1].toFixed(6)}{' '}
                    <a
                      href={`https://www.google.com/maps?q=${caseLocation.position[0]},${caseLocation.position[1]}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ color: '#1565c0', textDecoration: 'none', whiteSpace: 'nowrap' }}
                    >📍 Open map</a>
                    {caseLocation.reportedAt && (
                      <><br /><span style={{ color: '#555', fontSize: '12px' }}>
                        Reported: {new Date(caseLocation.reportedAt).toLocaleString()}
                      </span></>
                    )}
                    {isCell && cellStr && (
                      <><br /><span style={{ color: '#555', fontSize: '12px' }}>{cellStr}</span></>
                    )}
                  </span>
                </div>
              )}

              {locationLoading ? (
                <p className="cd-loc-message">Loading location data...</p>
              ) : hasData ? (
                <div className="cd-loc-map-wrapper">
                  <MapContainer
                    center={mapCenter}
                    zoom={13}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution="&copy; OpenStreetMap contributors"
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {locationTrail.length > 0 && (
                      <Polyline
                        positions={locationTrail.map(p => p.position)}
                        pathOptions={{ color: 'blue', weight: 4, opacity: 0.6 }}
                      />
                    )}

                    {locationTrail.map((point, index) => {
                      const isLatest = index === locationTrail.length - 1;

                      const latestIcon = new L.Icon({
                        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
                        iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
                        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
                        iconSize: [25, 41],
                        iconAnchor: [12, 41],
                        popupAnchor: [1, -34],
                        shadowSize: [41, 41],
                      });

                      return (
                        <RotatedMarker
                          key={index}
                          position={point.position}
                          rotationAngle={point.heading}
                          icon={isLatest ? latestIcon : undefined}
                          popupText={
                            <Popup>
                              {isLatest ? '🟢 Latest Glasses Location' : `#${index + 1}`}<br />
                              Date: {new Date(point.timestamp).toLocaleDateString()}<br />
                              Time: {new Date(point.timestamp).toLocaleTimeString()}<br />
                              Heading: {point.heading}°
                            </Popup>
                          }
                        />
                      );
                    })}

                    {caseLocation && caseIcon && (
                      <Marker position={caseLocation.position} icon={caseIcon}>
                        <Popup>
                          <strong>Case last location</strong><br />
                          Source: {isCell ? '📶 Cell tower (approx.)' : '🛰️ GPS'}<br />
                          {caseLocation.position[0].toFixed(6)}, {caseLocation.position[1].toFixed(6)}<br />
                          {caseLocation.reportedAt && (
                            <>Reported: {new Date(caseLocation.reportedAt).toLocaleString()}<br /></>
                          )}
                          {isCell && cellStr && (<>{cellStr}<br /></>)}
                          <a
                            href={`https://www.google.com/maps?q=${caseLocation.position[0]},${caseLocation.position[1]}`}
                            target="_blank" rel="noopener noreferrer"
                          >Open in Google Maps</a>
                        </Popup>
                      </Marker>
                    )}
                  </MapContainer>
                </div>
              ) : (
                <p  style={{ color: "black" }} className="cd-loc-message">No location data found.</p>
              )}
            </div>
          );
        }
        
      case 'texts':
      return chatUid ? <CustomerChat uid={chatUid} serial={chatSerial} /> : <p>Loading chat...</p>;

      case 'systemPreferences':
        return (
          <div className="admin-settings-tab">
            <SystemPreferences customerId={id} />
          </div>
        );

      case 'deviceHistory':
        return <DeviceHistoryPanel userId={id} />;

      case 'helpdeskTickets':
        return <HelpdeskPanel userId={id} mode="tickets" refreshKey={helpdeskRefresh} onCreate={() => setShowTicketModal(true)} />;

      case 'helpdeskEmails':
        return <HelpdeskPanel userId={id} userEmail={customer?.email} mode="emails" refreshKey={helpdeskRefresh} />;

      case 'wifiPreferences':
        return (
          <div className="admin-settings-tab">
            <WiFiPreferences customerId={id} />
          </div>
        );

      case 'deviceSettings':
        return (
          <div className="admin-settings-tab">
            <DeviceSettingsCard customerId={id} />
          </div>
        );

      default: return <div>Select a tab</div>;
    }
  };

  const toggleActive = async () => {
    const next = !customer.is_active;
    const msg = next
      ? `Activate ${customer.username}? They will be able to log in again.`
      : `Deactivate ${customer.username}? They will no longer be able to log in. This can be undone.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await fetch(`${BASE_URL}/api/admin/users/${id}/set-active/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('jwt_token')}` },
        body: JSON.stringify({ active: next }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Could not change status.');
      setCustomer((c) => ({ ...c, is_active: next }));
      setFormData((f) => ({ ...f, is_active: next }));
    } catch (e) { window.alert(e.message); }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="customer-detail-container">
      <div className="cd-bar">
        <button type="button" className="back-button cd-back" onClick={() => navigate(-1)}>
          <ArrowBackIcon style={{ fontSize: 18 }} /> Back
        </button>
        <div className="cd-id">
          <span className="cd-name">{customer ? ([customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.username) : ''}</span>
          <span className="cd-user">{customer && customer.username}</span>
          {customer && <ReservedBadge username={customer.username} />}
          {customer && <span className={`cd-pill ${customer.is_active ? 'on' : 'off'}`}>{customer.is_active ? 'ACTIVE' : 'INACTIVE'}</span>}
        </div>
        <div className="cd-actions">
          {customer && !customer.is_staff && !customer.is_superuser && (
            <button type="button" className={`cd-btn ${customer.is_active ? 'warn' : 'ok'}`} onClick={toggleActive}>
              {customer.is_active ? 'Deactivate' : 'Activate'}
            </button>
          )}
          <button type="button" className="cd-btn primary" onClick={() => setShowTicketModal(true)}>Create ticket</button>
        </div>
      </div>
      {customer && <TestAccountPanel userId={id} username={customer.username} />}
      {showTicketModal && (
        <CreateTicketModal
          userId={id}
          userLabel={customer ? ([customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.username) : ''}
          onClose={() => setShowTicketModal(false)}
          onCreated={(t) => { setShowTicketModal(false); setHelpdeskRefresh((n) => n + 1); setActiveTab('helpdeskTickets'); window.alert('Ticket ' + t.key + ' created.'); }}
        />
      )}
      <div className="tab-container">
        <ul className="tabs">
          <li className={`tab ${activeTab === 'customerDetails' ? 'active' : ''}`} onClick={() => setActiveTab('customerDetails')}>Customer Details</li>
          <li className={`tab ${activeTab === 'devices' ? 'active' : ''}`} onClick={() => setActiveTab('devices')}>Devices</li>
          <li className={`tab ${activeTab === 'deviceHistory' ? 'active' : ''}`} onClick={() => setActiveTab('deviceHistory')}>Device History</li>
          <li className={`tab ${activeTab === 'glassesStatusLogs' ? 'active' : ''}`} onClick={() => setActiveTab('glassesStatusLogs')}>Glasses Status Logs</li>
          <li className={`tab ${activeTab === 'caseStatusLogs' ? 'active' : ''}`} onClick={() => setActiveTab('caseStatusLogs')}>Case Status Logs</li>
          {/* Only show AI Logs tab when this customer has 'Keeping Logs' enabled in System Preferences */}
          {preferences?.logs_mode === 1 && (
            <li
              className={`tab ${activeTab === 'aiLogs' ? 'active' : ''}`}
              onClick={() => setActiveTab('aiLogs')}
            >
              AI Logs
            </li>
          )}
          <li className={`tab ${activeTab === 'calls' ? 'active' : ''}`} onClick={() => setActiveTab('calls')}>Calls</li>
            {/* Only show Glasses Logs tab if logs_mode and debug_mode are both enabled */}
          {preferences?.logs_mode === 1 && preferences?.debug_mode === 1 && (
            <li
              className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              Glasses Logs
            </li>
          )}
          <li className={`tab ${activeTab === 'caseLogs' ? 'active' : ''}`} onClick={() => setActiveTab('caseLogs')}>Case Logs</li>
          <li className={`tab ${activeTab === 'helpdeskTickets' ? 'active' : ''}`} onClick={() => setActiveTab('helpdeskTickets')}>Ticket History</li>
          <li className={`tab ${activeTab === 'helpdeskEmails' ? 'active' : ''}`} onClick={() => setActiveTab('helpdeskEmails')}>Email History</li>
          <li className={`tab ${activeTab === 'diagnostics' ? 'active' : ''}`} onClick={() => setActiveTab('diagnostics')}>Diagnostics</li>
          <li className={`tab ${activeTab === 'texts' ? 'active' : ''}`} onClick={() => setActiveTab('texts')}>Texts</li>
          <li className={`tab ${activeTab === 'locations' ? 'active' : ''}`} onClick={() => setActiveTab('locations')}>Locations</li>
          <li className={`tab ${activeTab === 'systemPreferences' ? 'active' : ''}`} onClick={() => setActiveTab('systemPreferences')}>System Preferences</li>
          <li className={`tab ${activeTab === 'wifiPreferences' ? 'active' : ''}`} onClick={() => setActiveTab('wifiPreferences')}>WiFi Preferences</li>
          <li className={`tab ${activeTab === 'deviceSettings' ? 'active' : ''}`} onClick={() => setActiveTab('deviceSettings')}>Device Settings</li>
          
        </ul>
      </div>
      <div className="tab-content">{renderTabContent()}</div>
    </div>
  );
};

export default CustomerDetail;
